// src/components/gavion/domain/dialer/contactCall.ts
// GAVION contact-call: calling one of a lead's contacts. The deep link is
// /appeler?company_id=<lead>&contact_id=<contact>&phone=<e164>; the number checks mirror SQL claim_contact_call
// (39_gavion_contact_call.sql): the phone must be one of the contact's phone_jsonb numbers once normalised.
import { normalizePhone } from "../phone";

export type ContactPhoneMatch = { e164: string; type: string | null };

/** The contact's number that `raw` designates (any spelling, E.164-normalised), or null. */
export function findContactPhone(
  phones: unknown,
  raw: unknown,
): ContactPhoneMatch | null {
  const e164 = normalizePhone(raw);
  if (!e164 || !Array.isArray(phones)) return null;
  for (const p of phones) {
    if (!p || typeof p !== "object") continue;
    const { number, type } = p as { number?: unknown; type?: unknown };
    if (normalizePhone(number) === e164)
      return { e164, type: typeof type === "string" && type ? type : null };
  }
  return null;
}

export const contactDisplayName = (c: {
  first_name?: string | null;
  last_name?: string | null;
}): string =>
  [c.first_name, c.last_name]
    .map((x) => (x ?? "").trim())
    .filter(Boolean)
    .join(" ");

/**
 * GAVION demo-contact: the contact's name, else its first email (a demo attendee booked without « Nom du participant »
 * has an address and no name, 68_gavion_demo_contact.sql), else "".
 */
export const contactLabel = (c: {
  first_name?: string | null;
  last_name?: string | null;
  email_jsonb?: unknown;
}): string => {
  const name = contactDisplayName(c);
  if (name) return name;
  for (const e of Array.isArray(c.email_jsonb) ? c.email_jsonb : []) {
    const email = (e as { email?: unknown } | null)?.email;
    if (typeof email === "string" && email.trim()) return email.trim();
  }
  return "";
};

/**
 * GAVION demo-contact (SQL twin: private.upsert_demo_contact, 68_gavion_demo_contact.sql): the first and last name
 * from « Nom du participant » — first word, the rest (booking_prepare's split); both "" without a name.
 */
export const splitAttendeeName = (
  raw: string | null | undefined,
): { first_name: string; last_name: string } => {
  const name = (raw ?? "").trim().replace(/\s+/g, " ").slice(0, 200);
  const at = name.indexOf(" ");
  return at < 0
    ? { first_name: name, last_name: "" }
    : { first_name: name.slice(0, at), last_name: name.slice(at + 1).trim() };
};

/** GAVION demo-contact: the lead's contact whose email_jsonb has `email` (trimmed, any case), the oldest one. */
export const findContactByEmail = <
  T extends { id: number; company_id?: unknown; email_jsonb?: unknown },
>(
  contacts: readonly T[],
  companyId: number,
  email: string,
): T | undefined => {
  const want = email.trim().toLowerCase();
  return [...contacts]
    .sort((a, b) => a.id - b.id)
    .find(
      (k) =>
        k.company_id === companyId &&
        (Array.isArray(k.email_jsonb) ? k.email_jsonb : []).some(
          (e) =>
            !!e &&
            typeof e === "object" &&
            String((e as { email?: unknown }).email ?? "")
              .trim()
              .toLowerCase() === want,
        ),
    );
};

/** The in-app dialer deep link for one contact number. */
export const contactCallPath = (
  companyId: number | string,
  contactId: number | string,
  e164: string,
): string =>
  `/appeler?${new URLSearchParams({
    company_id: String(companyId),
    contact_id: String(contactId),
    phone: e164,
  }).toString()}`;

/** contact_id + phone from the deep link query, or null (a plain lead dial). */
export function parseContactCallTarget(
  q: URLSearchParams,
): { contactId: number; phone: string } | null {
  const id = q.get("contact_id");
  const phone = normalizePhone(q.get("phone"));
  if (!id || !/^\d+$/.test(id) || !phone) return null;
  return { contactId: Number(id), phone };
}

/**
 * Follow-up M6: the contact's dialable numbers (NANP once normalised), in phone_jsonb order, each E.164 once (the
 * first spelling's type wins). Contacts list « Appeler »: one → one click, several → the rep picks.
 */
export function dialablePhones(phones: unknown): ContactPhoneMatch[] {
  const out: ContactPhoneMatch[] = [];
  for (const p of Array.isArray(phones) ? phones : []) {
    if (!p || typeof p !== "object") continue;
    const { number, type } = p as { number?: unknown; type?: unknown };
    const e164 = normalizePhone(number);
    if (!e164 || out.some((x) => x.e164 === e164)) continue;
    out.push({ e164, type: typeof type === "string" && type ? type : null });
  }
  return out;
}

/**
 * Contact recordings follow-up (SQL twin: v_contact_call in log_call_outcome, 41_gavion_contact_recordings.sql): the
 * call is not known to have dialled the lead's main number, so its outcome never marks that number bad. True for a
 * contact call (contact_id set), and for an outbound call without a number: a deleted contact's call (contact_id and
 * to_number cleared by the delete) or an erased lead's. A main-line call always carries the lead's number.
 */
export const offMainLine = (call: {
  contact_id?: unknown;
  direction?: unknown;
  to_number?: unknown;
  demo?: unknown;
}): boolean =>
  (call.contact_id != null && !demoLinked(call)) || // GAVION demo-contact
  (call.direction === "outbound" && call.to_number == null);

/**
 * GAVION demo-contact (SQL twin: v_linked in log_call_outcome, 68_gavion_demo_contact.sql): calls.contact_id was set
 * by « Démo réservée » on a main-line call (the attendee), so the call is still on the lead's main number.
 */
export const demoLinked = (call: { demo?: unknown }): boolean =>
  !!call.demo &&
  typeof call.demo === "object" &&
  (call.demo as { contact_linked?: unknown }).contact_linked === true;

/**
 * GAVION demo-contact (SQL twin: private.upsert_demo_contact's address check, 68_gavion_demo_contact.sql): an address
 * that can become the attendee's contact (trimmed, at most 254 characters, local@domain.tld without spaces or <>).
 */
export const isAttendeeEmail = (raw: string | null | undefined): boolean => {
  const email = (raw ?? "").trim().toLowerCase();
  return email.length <= 254 && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email);
};
